import { useEffect, useState } from "react";
import {
  ArrowLeft,
  User,
  Mail,
  Building,
  Phone,
  MapPin,
  Calendar,
  Edit2,
  Save,
  X,
  Camera,
} from "lucide-react";
import { useAuth } from "../../app/AuthContext";
import { useSchoolProfile } from "../../app/SchoolProfileContext";
import {
  getActivities,
  getClasses,
  getEnrollments,
  getStudents,
} from "../../lib/api";

interface ProfilePageProps {
  onBackClick?: () => void;
}

export default function ProfilePage({ onBackClick }: ProfilePageProps) {
  const { user, token, updateProfile } = useAuth();
  const { activeProfile, editProfile } = useSchoolProfile();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || "",
    email: user?.email || "",
    school: activeProfile?.institution || user?.school || "",
    phone: user?.phone || "",
    city: activeProfile?.city || user?.city || "",
    avatarUrl: user?.avatarUrl || "",
  });
  const [metrics, setMetrics] = useState({
    classes: 0,
    students: 0,
    activities: 0,
    loading: false,
  });

  // Synchronizes the form when the active profile or user changes
  useEffect(() => {
    if (!isEditing) {
      setFormData((prev) => ({
        ...prev,
        name: user?.name || "",
        email: user?.email || "",
        school: activeProfile?.institution || user?.school || "",
        phone: user?.phone || "",
        city: activeProfile?.city || user?.city || "",
        avatarUrl: user?.avatarUrl || "",
      }));
    }
  }, [user, activeProfile, isEditing]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        setMetrics((m) => ({ ...m, loading: true }));
        const [classes, students, activities, enrollments] = await Promise.all([
          getClasses(token, activeProfile?.id),
          getStudents(token, activeProfile?.id),
          getActivities(token, activeProfile?.id),
          getEnrollments(token, activeProfile?.id),
        ]);
        if (cancelled) return;
        const classIds = new Set(classes.map((c) => c.id));
        const scopedEnrollments = enrollments.filter((e) =>
          classIds.has(e.class_id),
        );
        const enrolledStudentIds = new Set(
          scopedEnrollments.map((e) => e.student_id),
        );
        setMetrics({
          classes: classes.length,
          students: enrolledStudentIds.size,
          activities: activities.length,
          loading: false,
        });
      } catch {
        if (cancelled) return;
        setMetrics((m) => ({ ...m, loading: false }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, activeProfile?.id]);

  const handleSave = async () => {
    try {
      // 1. Update user personal data
      await updateProfile({
        name: formData.name.trim() || undefined,
        phone: formData.phone.trim() || undefined,
        avatarUrl: formData.avatarUrl.trim() || undefined,
        // Maintains compatibility with fallback if no activeProfile
        school: !activeProfile
          ? formData.school.trim() || undefined
          : undefined,
        city: !activeProfile ? formData.city.trim() || undefined : undefined,
      });

      // 2. Update school data in the active profile
      if (activeProfile) {
        await editProfile(activeProfile.id, {
          institution: formData.school.trim() || undefined,
          city: formData.city.trim() || undefined,
        });
      }

      setIsEditing(false);
    } catch (error) {
      console.error("Erro ao salvar perfil:", error);
      alert("Erro ao salvar alterações. Tente novamente.");
    }
  };

  const handleCancel = () => {
    setFormData({
      name: user?.name || "",
      email: user?.email || "",
      school: activeProfile?.institution || user?.school || "",
      phone: user?.phone || "",
      city: activeProfile?.city || user?.city || "",
      avatarUrl: user?.avatarUrl || "",
    });
    setIsEditing(false);
  };

  const getInitials = () => {
    const name = user?.name?.trim();

    if (name) {
      const names = name.split(" ").filter(Boolean);

      const first = names[0]?.[0];
      const second = names[1]?.[0];

      if (first && second) {
        return `${first}${second}`.toUpperCase();
      }

      if (first) {
        return first.toUpperCase();
      }
    }

    const emailInitial = user?.email?.[0];
    return emailInitial ? emailInitial.toUpperCase() : "U";
  };

  return (
    <div className="min-h-screen bg-(--bg-secondary)">
      <header className="bg-(--header-bg) shadow-sm border-b border-(--border-color) sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16 gap-4">
            {onBackClick && (
              <button
                onClick={onBackClick}
                className="flex items-center gap-2 px-3 py-2 hover:bg-(--bg-accent) rounded-lg transition font-medium text-(--text-tertiary)"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Voltar</span>
              </button>
            )}
            <h1 className="text-xl font-semibold text-(--text-primary)">
              Meu Perfil
            </h1>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            <div className="bg-(--card-bg) rounded-xl shadow-sm border border-(--border-color) p-6 text-center">
              <div className="relative inline-block mb-4">
                {user?.avatarUrl ? (
                  <img
                    src={user.avatarUrl}
                    alt="Avatar"
                    className="w-32 h-32 rounded-full object-cover mx-auto bg-(--bg-accent)"
                  />
                ) : (
                  <div className="w-32 h-32 rounded-full flex items-center justify-center text-white font-bold text-4xl mx-auto bg-(--primary-color)">
                    {getInitials()}
                  </div>
                )}
                <button className="absolute bottom-0 right-0 w-10 h-10 rounded-full flex items-center justify-center shadow-lg hover:opacity-90 transition bg-(--primary-color)">
                  <Camera className="w-5 h-5 text-white" />
                </button>
              </div>

              <h2 className="text-2xl font-bold mb-1 text-(--text-primary)">
                {user?.name || "Professor"}
              </h2>
              <p className="text-sm mb-4 text-(--text-tertiary)">
                {user?.email}
              </p>

              {user?.school && (
                <div className="flex items-center justify-center gap-2 mb-4">
                  <Building className="w-4 h-4 text-(--text-tertiary)" />
                  <p className="text-sm text-(--text-tertiary)">
                    {user.school}
                  </p>
                </div>
              )}

              <div className="mt-6 pt-6 border-t border-(--border-color)">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-(--text-tertiary)">
                    Turmas Ativas
                  </span>
                  <span className="font-semibold text-(--text-primary)">
                    {metrics.loading ? "—" : metrics.classes}
                  </span>
                </div>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-(--text-tertiary)">
                    Total de Alunos
                  </span>
                  <span className="font-semibold text-(--text-primary)">
                    {metrics.loading ? "—" : metrics.students}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-(--text-tertiary)">
                    Atividades Criadas
                  </span>
                  <span className="font-semibold text-(--text-primary)">
                    {metrics.loading ? "—" : metrics.activities}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="bg-(--card-bg) rounded-xl shadow-sm border border-(--border-color) p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-(--text-primary)">
                  Informações Pessoais
                </h3>
                {!isEditing ? (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium hover:bg-(--bg-accent) transition border border-(--border-color) text-(--primary-color)"
                  >
                    <Edit2 className="w-4 h-4" />
                    Editar
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <button
                      onClick={handleCancel}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium hover:bg-(--bg-accent) transition border border-(--border-color) text-(--text-tertiary)"
                    >
                      <X className="w-4 h-4" />
                      Cancelar
                    </button>
                    <button
                      onClick={handleSave}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-white transition shadow-sm bg-(--primary-color)"
                    >
                      <Save className="w-4 h-4" />
                      Salvar
                    </button>
                  </div>
                )}
              </div>

              <div className="space-y-5">
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium mb-2 text-(--text-primary)">
                    <User className="w-4 h-4" />
                    Nome Completo
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      className="w-full px-4 py-3 border border-(--border-color) rounded-lg focus:outline-none focus:ring-2 transition bg-(--input-bg) text-(--text-primary)"
                    />
                  ) : (
                    <p className="px-4 py-3 rounded-lg bg-(--bg-secondary) text-(--text-primary)">
                      {formData.name || "Não informado"}
                    </p>
                  )}
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-medium mb-2 text-(--text-primary)">
                    <Mail className="w-4 h-4" />
                    Email
                  </label>
                  <p className="px-4 py-3 rounded-lg bg-(--bg-secondary) text-(--text-tertiary)">
                    {formData.email}
                    <span className="ml-2 text-xs">(não editável)</span>
                  </p>
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-medium mb-2 text-(--text-primary)">
                    <Building className="w-4 h-4" />
                    Instituição de Ensino
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={formData.school}
                      onChange={(e) =>
                        setFormData({ ...formData, school: e.target.value })
                      }
                      className="w-full px-4 py-3 border border-(--border-color) rounded-lg focus:outline-none focus:ring-2 transition bg-(--input-bg) text-(--text-primary)"
                    />
                  ) : (
                    <p className="px-4 py-3 rounded-lg bg-(--bg-secondary) text-(--text-primary)">
                      {formData.school || "Não informado"}
                    </p>
                  )}
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-medium mb-2 text-(--text-primary)">
                    <Phone className="w-4 h-4" />
                    Telefone
                  </label>
                  {isEditing ? (
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) =>
                        setFormData({ ...formData, phone: e.target.value })
                      }
                      placeholder="(00) 00000-0000"
                      className="w-full px-4 py-3 border border-(--border-color) rounded-lg focus:outline-none focus:ring-2 transition bg-(--input-bg) text-(--text-primary)"
                    />
                  ) : (
                    <p className="px-4 py-3 rounded-lg bg-(--bg-secondary) text-(--text-primary)">
                      {formData.phone || "Não informado"}
                    </p>
                  )}
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-medium mb-2 text-(--text-primary)">
                    <Camera className="w-4 h-4" />
                    Avatar (URL)
                  </label>
                  {isEditing ? (
                    <input
                      type="url"
                      value={formData.avatarUrl}
                      onChange={(e) =>
                        setFormData({ ...formData, avatarUrl: e.target.value })
                      }
                      placeholder="https://..."
                      className="w-full px-4 py-3 border border-(--border-color) rounded-lg focus:outline-none focus:ring-2 transition bg-(--input-bg) text-(--text-primary)"
                    />
                  ) : (
                    <p className="px-4 py-3 rounded-lg bg-(--bg-secondary) text-(--text-primary)">
                      {formData.avatarUrl || "Não informado"}
                    </p>
                  )}
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-medium mb-2 text-(--text-primary)">
                    <MapPin className="w-4 h-4" />
                    Cidade
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={formData.city}
                      onChange={(e) =>
                        setFormData({ ...formData, city: e.target.value })
                      }
                      placeholder="Fortaleza, CE"
                      className="w-full px-4 py-3 border border-(--border-color) rounded-lg focus:outline-none focus:ring-2 transition bg-(--input-bg) text-(--text-primary)"
                    />
                  ) : (
                    <p className="px-4 py-3 rounded-lg bg-(--bg-secondary) text-(--text-primary)">
                      {formData.city || "Não informado"}
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-(--border-color)">
                <div className="flex items-center gap-2 text-sm text-(--text-tertiary)">
                  <Calendar className="w-4 h-4" />
                  <span>
                    Membro desde{" "}
                    {user?.createdAt
                      ? new Date(user.createdAt).toLocaleString("pt-BR", {
                          day: "2-digit",
                          month: "long",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : new Date().toLocaleString("pt-BR", {
                          day: "2-digit",
                          month: "long",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
